import {
  Result,
  SelectList,
} from "api/baseApiResponseModel/BaseApiResponseModel";
import { IGeneralRepository } from "api/repositories/general/GeneralRepository";
import { TransactionDetailReportItem } from "api/repositories/general/model/TransactionDetailReportResponseModel";
import { TransactionReportItem } from "api/repositories/general/model/TransactionReportResponseModel";
import { defaultStoreRepository } from "api/repositories/store/StoreRepository";
import { defaultUserManagementRepository } from "api/repositories/userManagement/UserManagementRepository";
import { AuthenticationContext } from "context/AuthenticationContext";
import { isEmpty } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { DataFormat } from "utils/format/DataFormat";
import { getMessage } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";

const GeneralViewModel = (repository: IGeneralRepository) => {
  const [loading, setLoading] = useState(false);
  const [dataForCharts, setDataForCharts] = useState([]);
  const [revenue, setRevenue] = useState({});
  const [resultObject, setResultObject] = useState<Result | null>(null);
  const [transactionReportData, setTransactionReportData] = useState<
    TransactionReportItem[]
  >([]);
  const [transactionReportLoading, setTransactionReportLoading] =
    useState(false);
  const [transactionDetailReportData, setTransactionDetailReportData] =
    useState<TransactionDetailReportItem[]>([]);
  const [transactionDetailReportLoading, setTransactionDetailReportLoading] =
    useState(false);

  // Store and staff state management
  const [storeOptions, setStoreOptions] = useState<Array<SelectList>>([
    { label: strings.GlobalLabels.All, value: "" },
  ]);
  const [staffOptions, setStaffOptions] = useState<
    { label: string; value: string; type?: number }[]
  >([{ label: strings.GlobalLabels.All, value: "" }]);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [selectedStaffs, setSelectedStaffs] = useState<string[]>([]);
  const [staffLoading, setStaffLoading] = useState(false);

  const { config, user } = AuthenticationContext();

  const getDataForReports = async () => {
    try {
      setLoading(true);
      const res = await repository.getGeneralData();
      if (res?.data) {
        let temp = res?.data?.map((item) => {
          const time = moment(item?.time, "MM-YYYY").format("MM-YYYY");
          const data = {
            time: time,
            value: item?.total,
            count: item?.count,
          };
          return data;
        });
        setDataForCharts(temp);
        // setRevenue(res?.report || {});
      } else {
        setResultObject({
          type: "error",
          message: res?.message || strings.GlobalMessage.Error,
        });
      }
    } catch (error) {
      console.error(error);
      setResultObject({ type: "error", message: strings.GlobalMessage.Error });
    } finally {
      setLoading(false);
    }
  };

  const getTransactionReport = async (serviceCode: string) => {
    try {
      let data: any = serviceCode === "ALL" ? {} : { service: serviceCode };
      let dataStore = {};
      let dataStaff = {};
      const stores = Array.from(new Set(selectedStores.filter(Boolean)));
      const staffsRaw = Array.from(new Set(selectedStaffs.filter(Boolean)));
      const staffIdToType = new Map(
        staffOptions.map((opt) => [String(opt?.value), opt?.type])
      );
      const staffType4Ids = staffsRaw.filter(
        (id) => staffIdToType.get(String(id)) === 4
      );
      const staffType5Ids = staffsRaw.filter(
        (id) => staffIdToType.get(String(id)) === 5
      );
      const combinedStoreIds = Array.from(
        new Set([...(stores as string[]), ...staffType4Ids])
      );
      const staffs = Array.from(
        new Set(staffType5Ids.filter((id) => !combinedStoreIds.includes(id)))
      );
      const hasOverlap =
        combinedStoreIds.length > 0 &&
        staffType5Ids.some((id) => combinedStoreIds.includes(id));
      const hasType4 = staffType4Ids.length > 0;
      if (combinedStoreIds?.length > 0) {
        if (
          isEmpty(staffType5Ids) ||
          (!isEmpty(staffType4Ids) && !isEmpty(staffType5Ids))
        ) {
          combinedStoreIds?.forEach((item, index) => {
            if (!isEmpty(item)) {
              dataStore = {
                ...dataStore,
                [`store[${index}]`]: item,
              };
            } else {
              return;
            }
          });
        }
      }
      if (staffs?.length > 0) {
        staffs?.forEach((item, index) => {
          if (!isEmpty(item)) {
            dataStaff = {
              ...dataStaff,
              [`staff[${index}]`]: item,
            };
          } else {
            return;
          }
        });
      }
      // Set isOnlyStore=true nếu có staff type 4 hoặc có overlap
      data.isOnlyStore = hasType4 || hasOverlap ? "true" : undefined;
      setTransactionReportLoading(true);
      const res = await repository.getTransactionReport(
        DataFormat.removeUndefined({
          ...data,
          ...dataStore,
          ...dataStaff,
        })
      );
      if (res?.data) {
        setTransactionReportData(res.data);
      } else {
        setTransactionReportData([]);
        setResultObject({
          type: "error",
          message: res?.message || strings.GlobalMessage.Error,
        });
      }
    } catch (error) {
      console.error(error);
      getMessage(error?.message, 3, "error");
    } finally {
      setTransactionReportLoading(false);
    }
  };

  const getTransactionDetailReport = async (
    serviceCode: string,
    reportType: number
  ) => {
    try {
      if (serviceCode !== "ALL") {
        setTransactionDetailReportLoading(true);
        let data: any = serviceCode === "ALL" ? {} : { service: serviceCode };
        let dataStore = {};
        let dataStaff = {};
        data.type = reportType;
        const stores = Array.from(new Set(selectedStores.filter(Boolean)));
        const staffsRaw = Array.from(new Set(selectedStaffs.filter(Boolean)));
        const staffIdToType = new Map(
          staffOptions.map((opt) => [String(opt?.value), opt?.type])
        );
        const staffType4Ids = staffsRaw.filter(
          (id) => staffIdToType.get(String(id)) === 4
        );
        const staffType5Ids = staffsRaw.filter(
          (id) => staffIdToType.get(String(id)) === 5
        );
        const combinedStoreIds = Array.from(
          new Set([...(stores as string[]), ...staffType4Ids])
        );
        const staffs = Array.from(
          new Set(staffType5Ids.filter((id) => !combinedStoreIds.includes(id)))
        );
        const hasOverlap =
          combinedStoreIds.length > 0 &&
          staffType5Ids.some((id) => combinedStoreIds.includes(id));
        const hasType4 = staffType4Ids.length > 0;
        if (combinedStoreIds?.length > 0) {
          if (
            isEmpty(staffType5Ids) ||
            (!isEmpty(staffType4Ids) && !isEmpty(staffType5Ids))
          ) {
            combinedStoreIds?.forEach((item, index) => {
              if (!isEmpty(item)) {
                dataStore = {
                  ...dataStore,
                  [`store[${index}]`]: item,
                };
              } else {
                return;
              }
            });
          }
        }
        if (staffs?.length > 0) {
          staffs?.forEach((item, index) => {
            if (!isEmpty(item)) {
              dataStaff = {
                ...dataStaff,
                [`staff[${index}]`]: item,
              };
            } else {
              return;
            }
          });
        }
        data.isOnlyStore = hasType4 || hasOverlap ? "true" : undefined;
        const res = await repository.getTransactionDetailReport(
          DataFormat.removeUndefined({
            ...data,
            ...dataStore,
            ...dataStaff,
          })
        );
        if (res?.data) {
          setTransactionDetailReportData(res.data);
        } else {
          setTransactionDetailReportData([]);
          setResultObject({
            type: "error",
            message: res?.message || strings.GlobalMessage.Error,
          });
        }
      }
    } catch (error) {
      console.error(error);
      getMessage(error?.message, 3, "error");
    } finally {
      setTransactionDetailReportLoading(false);
    }
  };

  const exportTransactionDetailReport = async (
    serviceCode: string,
    reportType: number,
    extras?: { store?: string; staff?: string }
  ) => {
    let dataStore = {};
    let dataStaff = {};
    const data: any = serviceCode === "ALL" ? {} : { service: serviceCode };
    data.type = reportType;
    if (extras?.store) data.store = extras.store;
    if (extras?.staff) data.staff = extras.staff;

    // Normalize to arrays
    const storesInput = Array.isArray(data.store)
      ? Array.from(new Set((data.store as string[]).filter(Boolean)))
      : data.store
      ? [data.store as string]
      : [];
    const staffsInputRaw = Array.isArray(data.staff)
      ? Array.from(new Set((data.staff as string[]).filter(Boolean)))
      : data.staff
      ? [data.staff as string]
      : [];

    // Route staff by type: 4 -> store[], 5 -> staff[]
    const staffIdToType = new Map(
      staffOptions.map((opt) => [String(opt?.value), opt?.type])
    );
    const staffType4Ids = staffsInputRaw.filter(
      (id) => staffIdToType.get(String(id)) === 4
    );
    const staffType5Ids = staffsInputRaw.filter(
      (id) => staffIdToType.get(String(id)) === 5
    );
    const combinedStoreIds = Array.from(
      new Set([...(storesInput as string[]), ...staffType4Ids])
    );
    const staffs = Array.from(
      new Set(staffType5Ids.filter((id) => !combinedStoreIds.includes(id)))
    );
    const hasOverlap =
      combinedStoreIds.length > 0 &&
      staffType5Ids.some((id) => combinedStoreIds.includes(id));
    const hasType4 = staffType4Ids.length > 0;

    if (combinedStoreIds?.length > 0) {
      if (
        isEmpty(staffType5Ids) ||
        (!isEmpty(staffType4Ids) && !isEmpty(staffType5Ids))
      ) {
        combinedStoreIds?.forEach((item, index) => {
          if (!isEmpty(item)) {
            dataStore = {
              ...dataStore,
              [`store[${index}]`]: item,
            };
          } else {
            return;
          }
        });
      }
    }
    if (staffs?.length > 0) {
      staffs?.forEach((item, index) => {
        if (!isEmpty(item)) {
          dataStaff = {
            ...dataStaff,
            [`staff[${index}]`]: item,
          };
        } else {
          return;
        }
      });
    }
    data.isOnlyStore = hasType4 || hasOverlap ? "true" : undefined;

    delete data?.store;
    delete data?.staff;
    const res = await repository.exportTransactionDetailReport(
      DataFormat.removeUndefined({
        ...data,
        ...dataStore,
        ...dataStaff,
      })
    );
    return res?.data as unknown as Blob;
  };

  const loadStores = async () => {
    try {
      if (user.type === 3 || user.type === 4) {
        const res = await defaultStoreRepository.getStoreList({
          page: 0,
          limit: 500,
          status: 0,
        } as any);
        const items = res?.data?.data?.items || [];
        const opts = [
          { label: strings.GlobalLabels.All, value: "" },
          ...items.map((s) => ({
            label: s?.name,
            value: s?.userId,
            code: s?.code,
          })),
        ];
        if (user.type === 4) {
          const myStore = items.find((s) => s?.userId === user?.id);
          if (myStore) {
            setStoreOptions([
              {
                label: myStore?.name,
                value: myStore?.userId as any,
                code: myStore?.code,
              },
            ] as any);
            setSelectedStores([String(myStore?.userId || "")] as any);
            // Trigger staff loading immediately for store users so the API receives the store code on first load
            loadStaff();
          } else {
            setStoreOptions(opts);
          }
        } else {
          setStoreOptions(opts);
        }
      }
    } catch (e) {
      setStoreOptions([{ label: strings.GlobalLabels.All, value: "" }]);
    }
  };

  const loadStaff = async () => {
    try {
      setStaffLoading(true);
      if (selectedStores.length === 0 && user.type !== 4) {
        setStaffOptions([{ label: strings.GlobalLabels.All, value: "" }]);
        setSelectedStaffs([]);
        setStaffLoading(false);
        return;
      }
      if (user.type === 3 || user.type === 4) {
        let selectedCodes = isEmpty(selectedStores?.at(-1))
          ? []
          : storeOptions
              ?.filter(
                (ele) =>
                  !isEmpty(ele?.value) && selectedStores?.includes(ele?.value)
              )
              ?.map((child) => child?.code) || [];
        // If store-level user has no explicit selected store, fallback to their store
        if (user.type === 4 && selectedCodes.length === 0) {
          const myStore = storeOptions.find((s: any) => s?.value === user?.id);
          if (myStore?.code) {
            selectedCodes = [myStore.code];
          } else {
            const anyStoreWithCode = storeOptions.find((s: any) => !!s?.code);
            if (anyStoreWithCode?.code) {
              selectedCodes = [anyStoreWithCode.code];
            }
          }
        }
        const res = await defaultUserManagementRepository.getUserListV2({
          page: 0,
          limit: 500,
          status: 0,
          ...(selectedCodes.length > 0 ? { store: selectedCodes } : {}),
        } as any);
        const items = res?.data?.items || [];
        const opts = [
          { label: strings.GlobalLabels.All, value: "" },
          ...items.map((u) => ({
            label: u?.fullName || u?.userName || u?.phone,
            value: u?.id,
            type: u?.type,
          })),
        ];
        setStaffOptions(opts);
      }
    } catch (e) {
      setStaffOptions([{ label: strings.GlobalLabels.All, value: "" }]);
    } finally {
      setStaffLoading(false);
    }
  };

  // Load stores on component mount
  useEffect(() => {
    loadStores();
  }, [strings, user?.id, user?.type]);

  // Load staff when selectedStores changes
  useEffect(() => {
    loadStaff();
  }, [selectedStores, strings]);

  // Clear staff selections when stores cleared
  useEffect(() => {
    if (selectedStores.length === 0 && selectedStaffs.length > 0) {
      setSelectedStaffs([]);
    }
  }, [selectedStores]);

  return {
    loading,
    dataForCharts,
    revenue,
    resultObject,
    config,
    transactionReportData,
    transactionReportLoading,
    getTransactionReport,
    transactionDetailReportData,
    transactionDetailReportLoading,
    getTransactionDetailReport,
    exportTransactionDetailReport,
    // Store and staff related
    storeOptions,
    staffOptions,
    selectedStores,
    selectedStaffs,
    setSelectedStores,
    setSelectedStaffs,
    loadStores,
    loadStaff,
    staffLoading,
  };
};

export default GeneralViewModel;
